import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Container, TextField, MenuItem, Grid, Card,
  CardContent, Button, Chip, CircularProgress, InputAdornment, Fade,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import BalanceIcon from '@mui/icons-material/Balance';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface TribunaleInfo {
  tribunale: string;
  citta: string;
  regione: string;
  nGiudici: number;
  nSentenze: number;
}

interface GiudiceInfo {
  nome: string;
  nSentenze: number;
}

export default function BookyGiudicePage() {
  const navigate = useNavigate();
  const [tribunali, setTribunali] = useState<TribunaleInfo[]>([]);
  const [loadingTribunali, setLoadingTribunali] = useState(true);
  const [regioneSelezionata, setRegioneSelezionata] = useState('');
  const [tribunaleSelezionato, setTribunaleSelezionato] = useState('');
  const [giudici, setGiudici] = useState<GiudiceInfo[]>([]);
  const [loadingGiudici, setLoadingGiudici] = useState(false);
  const [giudiceSelezionato, setGiudiceSelezionato] = useState<GiudiceInfo | null>(null);
  const [cercaGiudice, setCercaGiudice] = useState('');

  useEffect(() => {
    axios.get('/api/giudici/tribunali').then(r => {
      setTribunali(r.data);
    }).finally(() => setLoadingTribunali(false));
  }, []);

  const regioni = useMemo(() => {
    const set = new Set(tribunali.map(t => t.regione));
    return Array.from(set).sort();
  }, [tribunali]);

  const tribunaliFiltrati = useMemo(() => {
    if (!regioneSelezionata) return [];
    return tribunali.filter(t => t.regione === regioneSelezionata);
  }, [tribunali, regioneSelezionata]);

  useEffect(() => {
    if (!tribunaleSelezionato) { setGiudici([]); return; }
    setLoadingGiudici(true);
    setGiudiceSelezionato(null);
    axios.get(`/api/giudici/lista?tribunale=${encodeURIComponent(tribunaleSelezionato)}`)
      .then(r => setGiudici(r.data))
      .finally(() => setLoadingGiudici(false));
  }, [tribunaleSelezionato]);

  const giudiciFiltrati = useMemo(() => {
    if (!cercaGiudice.trim()) return giudici;
    return giudici.filter(g => g.nome.toLowerCase().includes(cercaGiudice.toLowerCase()));
  }, [giudici, cercaGiudice]);

  const handleInizia = () => {
    if (!giudiceSelezionato) return;
    navigate('/booky-giudice/sessione', {
      state: { nome: giudiceSelezionato.nome, tribunale: tribunaleSelezionato },
    });
  };

  const tInfo = tribunali.find(t => t.tribunale === tribunaleSelezionato);

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0d1b2a 0%, #1a2f4a 60%, #0d1b2a 100%)' }}>

      {/* ── COLONNE DECORATIVE ── */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        {[0, 1, 2, 3, 4].map(i => (
          <Box key={i} sx={{
            position: 'absolute', top: 0,
            left: `${10 + i * 20}%`,
            width: 3, height: 280,
            background: 'linear-gradient(180deg, rgba(184,134,11,0.4) 0%, transparent 100%)',
            borderRadius: 2,
          }} />
        ))}

        {/* HEADER COURTHOUSE */}
        <Container maxWidth="lg" sx={{ pt: 6, pb: 4, position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <BalanceIcon sx={{ fontSize: 52, color: '#B8860B', mb: 1 }} />
          </Box>
          <Typography variant="h3" sx={{
            fontFamily: '"Syne", sans-serif',
            fontWeight: 800,
            color: '#F5E6C0',
            textAlign: 'center',
            letterSpacing: '0.04em',
            textShadow: '0 2px 20px rgba(0,0,0,0.5)',
            mb: 1,
          }}>
            BOOKY Giudice
          </Typography>
          <Typography sx={{
            textAlign: 'center', color: 'rgba(245,230,192,0.6)',
            fontSize: 15, mb: 4, fontStyle: 'italic',
          }}>
            Seleziona il giudice e scopri come giudica. Interroga il suo profilo AI costruito sulle sue sentenze reali.
          </Typography>

          {/* BARRA DECORATIVA */}
          <Box sx={{
            mx: 'auto', width: 160, height: 3,
            background: 'linear-gradient(90deg, transparent, #B8860B, transparent)',
            mb: 5,
          }} />

          {/* ── FILTRI ── */}
          <Grid container spacing={3} justifyContent="center" sx={{ mb: 5 }}>
            {/* Regione */}
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 1 }}>
                <Typography sx={{ color: '#B8860B', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', mb: 0.5 }}>
                  1 — Seleziona Regione
                </Typography>
              </Box>
              <TextField
                select fullWidth
                value={regioneSelezionata}
                onChange={e => { setRegioneSelezionata(e.target.value); setTribunaleSelezionato(''); setGiudiceSelezionato(null); }}
                placeholder="Scegli una regione..."
                disabled={loadingTribunali}
                sx={fieldStyle}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="" disabled><em style={{ color: '#666' }}>— Scegli una regione —</em></MenuItem>
                {regioni.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Grid>

            {/* Tribunale */}
            <Grid item xs={12} md={4}>
              <Typography sx={{ color: '#B8860B', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', mb: 0.5 }}>
                2 — Seleziona Tribunale
              </Typography>
              <TextField
                select fullWidth
                value={tribunaleSelezionato}
                onChange={e => { setTribunaleSelezionato(e.target.value); setGiudiceSelezionato(null); }}
                disabled={!regioneSelezionata}
                sx={fieldStyle}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="" disabled><em style={{ color: '#666' }}>— Scegli un tribunale —</em></MenuItem>
                {tribunaliFiltrati.map(t => (
                  <MenuItem key={t.tribunale} value={t.tribunale}>
                    {t.tribunale} &nbsp;
                    <Chip label={`${t.nGiudici} giudici`} size="small" sx={{ ml: 1, fontSize: 10, height: 18 }} />
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {/* INFO TRIBUNALE */}
          {tInfo && (
            <Fade in>
              <Box sx={{
                mx: 'auto', maxWidth: 600, mb: 4, textAlign: 'center',
                background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.25)',
                borderRadius: 2, py: 1.5, px: 3,
              }}>
                <Typography sx={{ color: '#F5E6C0', fontSize: 13 }}>
                  <strong style={{ color: '#B8860B' }}>{tInfo.tribunale}</strong>
                  &nbsp;·&nbsp; {tInfo.nGiudici} giudici nel profilo &nbsp;·&nbsp; {tInfo.nSentenze} sentenze analizzate
                </Typography>
              </Box>
            </Fade>
          )}
        </Container>
      </Box>

      {/* ── LISTA GIUDICI ── */}
      <Container maxWidth="lg" sx={{ pb: 8 }}>
        {loadingGiudici && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#B8860B' }} />
            <Typography sx={{ color: 'rgba(245,230,192,0.5)', mt: 2 }}>Caricamento giudici...</Typography>
          </Box>
        )}

        {giudici.length > 0 && !loadingGiudici && (
          <Fade in>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography sx={{ color: '#B8860B', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  3 — Scegli il Giudice
                </Typography>
                <TextField
                  size="small"
                  placeholder="Cerca per nome..."
                  value={cercaGiudice}
                  onChange={e => setCercaGiudice(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#B8860B', fontSize: 18 }} /></InputAdornment> }}
                  sx={{ ...fieldStyle, width: 220 }}
                />
              </Box>

              <Grid container spacing={2}>
                {giudiciFiltrati.map(g => {
                  const selected = giudiceSelezionato?.nome === g.nome;
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={g.nome}>
                      <Card
                        onClick={() => setGiudiceSelezionato(selected ? null : g)}
                        sx={{
                          cursor: 'pointer',
                          background: selected
                            ? 'linear-gradient(135deg, #B8860B22, #B8860B44)'
                            : 'rgba(255,255,255,0.04)',
                          border: selected ? '2px solid #B8860B' : '1px solid rgba(255,255,255,0.08)',
                          borderRadius: 2,
                          transition: 'all 0.2s',
                          '&:hover': {
                            border: '1px solid rgba(184,134,11,0.5)',
                            background: 'rgba(184,134,11,0.08)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        <CardContent sx={{ py: 2, px: 2.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box sx={{
                              width: 36, height: 36, borderRadius: '50%',
                              background: selected ? '#B8860B' : 'rgba(184,134,11,0.2)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                              <PersonIcon sx={{ fontSize: 18, color: selected ? '#fff' : '#B8860B' }} />
                            </Box>
                            <Box>
                              <Typography sx={{ color: '#F5E6C0', fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>
                                {g.nome}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <GavelIcon sx={{ fontSize: 12, color: 'rgba(245,230,192,0.4)' }} />
                            <Typography sx={{ color: 'rgba(245,230,192,0.5)', fontSize: 11 }}>
                              {g.nSentenze} {g.nSentenze === 1 ? 'sentenza' : 'sentenze'} nel profilo
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {giudiciFiltrati.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography sx={{ color: 'rgba(245,230,192,0.4)' }}>Nessun giudice trovato per "{cercaGiudice}"</Typography>
                </Box>
              )}
            </Box>
          </Fade>
        )}

        {!loadingTribunali && !tribunaleSelezionato && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <LocationOnIcon sx={{ fontSize: 48, color: 'rgba(184,134,11,0.3)', mb: 2 }} />
            <Typography sx={{ color: 'rgba(245,230,192,0.35)', fontSize: 15 }}>
              Seleziona regione e tribunale per vedere i giudici disponibili
            </Typography>
          </Box>
        )}
      </Container>

      {/* ── PULSANTE FISSO IN BASSO ── */}
      {giudiceSelezionato && (
        <Fade in>
          <Box sx={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
            background: 'linear-gradient(180deg, transparent, #0d1b2a 30%)',
            pt: 4, pb: 3, px: 3,
            display: 'flex', justifyContent: 'center',
          }}>
            <Button
              onClick={handleInizia}
              size="large"
              startIcon={<GavelIcon />}
              sx={{
                background: 'linear-gradient(135deg, #B8860B, #DAA520)',
                color: '#0d1b2a',
                fontWeight: 800,
                fontSize: 15,
                px: 5, py: 1.5,
                borderRadius: 2,
                boxShadow: '0 4px 24px rgba(184,134,11,0.5)',
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #DAA520, #B8860B)',
                  transform: 'scale(1.03)',
                },
                transition: 'all 0.2s',
              }}
            >
              Inizia la Sessione con {giudiceSelezionato.nome}
            </Button>
          </Box>
        </Fade>
      )}
    </Box>
  );
}

const fieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: '#F5E6C0',
    background: 'rgba(255,255,255,0.05)',
    '& fieldset': { borderColor: 'rgba(184,134,11,0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(184,134,11,0.6)' },
    '&.Mui-focused fieldset': { borderColor: '#B8860B' },
  },
  '& .MuiSelect-icon': { color: '#B8860B' },
  '& .MuiInputBase-input': { color: '#F5E6C0' },
};
